import React, { useState, useEffect } from 'react';
import { FormikProps, FormikValues } from 'formik';
import {
  Spinner as ReactSpinner,
  Alert as ReactAlert,
  TextField as ReactTextField,
  TextArea as ReactTextArea,
  Select,
  Button as ReactButton,
} from '@a-ui/react';

import { DetailLayout } from 'components/DetailLayout';
import { translate } from 'i18n';
import Form from 'components/Form';

import { FormHelpers } from 'components/Form/Form';
import { AxiosError } from 'axios';
import { api } from 'hooks/api.hooks';
// import { setMessage } from 'state/Message/actions';
import { ITranslations } from 'i18n/config';
import Box from 'components/Box';
import Typography from 'components/Typography';
import { initialValues, validationSchema } from './ACardTransferConfig';

export interface ACardTransferOverviewProps {
  receiver?: any;
  acardNumber?: string;
  cheque?: string;
  remark: string;
  isLoading: boolean;
  setStep: (step: number) => void;
  handleSubmit: (
    acardNumber: string | undefined,
    cheque: string | undefined,
    remark: string | undefined,
  ) => void;
}

const ACardTransferOverview: React.FC<ACardTransferOverviewProps> = ({
  receiver,
  acardNumber,
  cheque,
  remark,
  isLoading,
  setStep,
  handleSubmit,
}) => {
  return (
    <>
      <Typography variant="h2" as="h6" classNames="u-margin-bottom">
        {translate('acard.transfer.add.form.submit')}
      </Typography>

      <Typography>
        {translate('acard.transfer.add.form.submit.description', {
          name: (<b>{receiver.cardholders[0].cardholderFirstname}</b>) as unknown as string,
        })}
      </Typography>

      <dl className="a-definition-list u-margin-top">
        <dt>A-kaartnummer van de ontvanger:</dt>
        <dd>{acardNumber}</dd>
        <dt>{translate('acard.transfer.add.form.field.cheque.label')}:</dt>
        <dd>{cheque}</dd>
        <dt>Jouw bericht:</dt>
        <dd style={{ wordBreak: 'break-word' }}>{remark && remark.substring(0, 140)}</dd>
      </dl>

      <div className="m-button-group u-margin-top">
        <div className="u-margin-top u-margin-right">
          <ReactButton
            htmlType="button"
            {...(isLoading ? { addOn: { type: 'spinner', align: 'left' } } : {})}
            onClick={() => setStep(0)}
          >
            {translate('common.button.back')}
          </ReactButton>
        </div>
        <div className="u-margin-top">
          <ReactButton
            htmlType="button"
            theme="success"
            {...(isLoading ? { addOn: { type: 'spinner', align: 'left' } } : {})}
            onClick={() => handleSubmit(acardNumber, cheque, remark)}
          >
            {translate('acard.transfer.add.form.field.submit.label')}
          </ReactButton>
        </div>
      </div>
    </>
  );
};

export interface ACardTransferSuccessProps {
  acardNumber?: string;
  cheque?: string;
  remark: string;
  receiver?: any;
}

const ACardTransferSuccess: React.FC<ACardTransferSuccessProps> = ({
  acardNumber,
  cheque,
  remark,
  receiver,
}) => {
  return (
    <>
      <Typography variant="h2" as="h6" classNames="u-margin-bottom">
        {translate('acard.transfer.add.form.success.message')}
      </Typography>
      <div className="u-margin-bottom">
        <ReactAlert theme="success">
          <Typography>
            {translate('acard.transfer.add.form.success.message.description', {
              name: (<b>{receiver.cardholders[0].cardholderFirstname}</b>) as unknown as string,
            })}
          </Typography>
        </ReactAlert>
      </div>
      <dl className="a-definition-list">
        <dt>A-kaartnummer van de ontvanger:</dt>
        <dd>{acardNumber}</dd>
        <dt>{translate('acard.transfer.add.form.field.cheque.label')}:</dt>
        <dd>{cheque}</dd>
        <dt>Jouw bericht:</dt>
        <dd style={{ wordBreak: 'break-word' }}>{remark && remark.substring(0, 140)}</dd>
      </dl>
    </>
  );
};

export interface ACardTransferFormProps {
  selectOptions: { label: number; value: number }[];
  values: any;
  errors: any;
  touched: any;
  isLoading: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ACardTransferForm: React.FC<ACardTransferFormProps> = ({
  selectOptions,
  errors,
  isLoading,
  handleChange,
}) => {
  return (
    <>
      <Typography variant="h2" as="h6" classNames="u-margin-bottom">
        {translate('acard.transfer.add.title')}
      </Typography>

      <div className="u-margin-bottom">
        <ReactTextField
          label={translate('acard.transfer.add.form.field.acardnumber.label')}
          description={translate('acard.transfer.add.form.field.acardnumber.description')}
          {...(errors.acardNumber && {
            state: 'error',
            description: translate(errors.acardNumber as ITranslations),
          })}
          onChange={handleChange}
          name="acardNumber"
        />
      </div>

      <div className="u-margin-bottom">
        <Select
          name="cheque"
          label={translate('acard.transfer.add.form.field.cheque.label')}
          description={translate('acard.transfer.add.form.field.cheque.description')}
          required={true}
          options={selectOptions}
          onChange={handleChange}
          {...(errors.cheque && {
            state: 'error',
            description: translate(errors.cheque as ITranslations),
          })}
        />
      </div>

      <ReactTextArea
        label={translate('acard.transfer.add.form.field.remark.label')}
        description={translate('acard.transfer.add.form.field.remark.description')}
        className="u-margin-top"
        required={false}
        onChange={(e: any) => {
          e.target.value = e.target.value.replace(/[\\'"]/g, '').substring(0, 140);
          handleChange(e);
        }}
        maxLength={140}
        name="remark"
      />
      <div className="u-margin-top">
        <ReactButton
          fullWidth
          htmlType="submit"
          {...(isLoading ? { addOn: { type: 'spinner', align: 'left' } } : {})}
        >
          {translate('common.button.next')}
        </ReactButton>
      </div>
    </>
  );
};

const ACardTransfer: React.FC = () => {
  const form = React.useRef<FormikProps<FormikValues>>(null);
  const [cheques, setCheques] = useState<Api.ACardCheque[]>([]);
  const [step, setStep] = useState(0);
  const [receiver, setReceiver] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<ITranslations>();

  const selectOptions: { label: number; value: number }[] = [];

  for (let i = 0; i < cheques.length; i++) {
    selectOptions.push({
      value: i + 1,
      label: i + 1,
    });
  }

  const fetchACardProfile = async () => {
    setLoading(true);
    const { data } = await api.get<Api.ACardProfile>('/acard/account-info/me');
    if (!data) {
      setLoading(false);
      setError('acard.transfer.add.form.errors.default');
    }
    if (data) {
      if (data.cheques) {
        setCheques(
          data.cheques.sort((a: Api.ACardCheque, b: Api.ACardCheque) => {
            // oldest cheques first.
            return new Date(b.chequeValidTill).valueOf() + new Date(a.chequeValidTill).valueOf();
          }),
        );
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchACardProfile();
  }, []);

  const handleValidation = async (
    values: { acardNumber: string; cheque: string; remark: string },
    { enableValidation }: FormHelpers,
  ) => {
    setLoading(true);
    setError(undefined);

    try {
      const { data } = await api.get<Api.ACardProfile>(
        `/acard/account-info/${values.acardNumber.replace(/\D/g, '')}`,
      );

      setReceiver(data);
      enableValidation(false);
      setLoading(false);
      setError(undefined);
      setStep(1);
    } catch (e) {
      const { response } = e as AxiosError;
      setLoading(false);

      if (response?.data?.code === '3063') {
        setError('acard.transfer.add.form.errors.3081');
      } else if (response?.data?.code === '3081') {
        setError('acard.transfer.add.form.errors.3081');
      } else {
        setError('acard.transfer.add.form.errors.default');
      }
    }
  };

  const handleSubmit = async (acardNumber: string, cheque: string, remark: string) => {
    setLoading(true);
    setError(undefined);

    const chequeIds = [];
    for (let i = 0; i < parseInt(cheque, 10); i++) {
      chequeIds.push(cheques[i].chequeId);
    }

    try {
      await api.post('/acard/transfer', {
        acardNumber,
        remark: remark.substring(0, 140),
        chequeIds,
      });

      setStep(2);
      setLoading(false);
    } catch (e) {
      const { response } = e as AxiosError;

      setLoading(false);

      if (response?.status === 422) {
        if (response?.data?.code === '3063') {
          setError('acard.transfer.add.form.errors.3081.description');
        }

        if (response?.data?.code === '3081') {
          setError('acard.transfer.add.form.errors.3081.description');
        }
      } else {
        setError('acard.transfer.add.form.errors.default.description');
      }
    }
  };

  return (
    <DetailLayout
      backAction="/a-kaart"
      // backLabel="accessKeys.overview.title"
      title="acard.transfer.title"
      description="acard.transfer.body"
    >
      <Box>
        {isLoading && <ReactSpinner />}
        {error && (
          <div className="u-margin-top-xs u-margin-bottom">
            <ReactAlert inline theme="danger">
              <Typography>{translate(error)}</Typography>
            </ReactAlert>
          </div>
        )}

        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleValidation}
          ref={form}
        >
          {({ errors, touched, values, handleChange }) => {
            if (cheques.length === 0 && !isLoading) {
              return (
                <div className="u-margin-bottom">
                  <ReactAlert inline theme="warning">
                    <Typography>{translate('acard.transfer.add.cheques.empty')}</Typography>
                  </ReactAlert>
                </div>
              );
            }

            if (step === 1) {
              return (
                <ACardTransferOverview
                  cheques={cheques}
                  {...values}
                  setStep={setStep}
                  receiver={receiver}
                  handleSubmit={handleSubmit}
                />
              );
            }

            if (step === 2) {
              return <ACardTransferSuccess receiver={receiver} cheques={cheques} {...values} />;
            }

            return (
              <ACardTransferForm
                handleChange={handleChange}
                errors={errors}
                touched={touched}
                selectOptions={selectOptions}
                values={values}
                isLoading={isLoading}
              />
            );
          }}
        </Form>
      </Box>
    </DetailLayout>
  );
};

export default ACardTransfer;
